import React, { useCallback, useMemo } from "react";
import useKeyPress from "../../hooks/useKeyPress";
import ChevronIcon from "../Icons/ChevronIcon";
import Button from "../Button";
import Camera from "../Icons/Camera";

import styles from "./imageGalleryGrid.module.scss";

type ImageData = {
  id: number,
  width: number,
  height: number,
  src: string
};

type Props = {
  currentIndex: number,
  setIndex: (index: number) => void,
  images: Array<ImageData>,
  onButtonClick: () => void,
  onImageClick: (number) => void
};

const DesktopImageView = ({
  currentIndex,
  images,
  setIndex,
  onButtonClick,
  onImageClick
}: Props) => {
  const imageCount = images.length;

  const isFirstImage = currentIndex === 0;
  const isLastImage = imageCount === currentIndex + 1;

  const onClickPrev = useCallback(() => {
    if (isFirstImage) return;

    const index = currentIndex === 0 ? imageCount - 1 : currentIndex - 1;
    setIndex(index);
  }, [currentIndex, setIndex, imageCount, isFirstImage]);

  const onClickNext = useCallback(() => {
    if (isLastImage) return;

    const index = currentIndex === imageCount - 1 ? 0 : currentIndex + 1;

    setIndex(index);
  }, [currentIndex, setIndex, imageCount, isLastImage]);

  const image = useMemo(() => {
    return images[currentIndex || 0];
  }, [currentIndex, images]);

  useKeyPress("ArrowLeft", onClickPrev);
  useKeyPress("ArrowRight", onClickNext);

  return (
    <div
      className={`${styles.aspectRatioBox} ${styles.desktopImage} ${styles.container}`}
    >
      <button onClick={() => onImageClick(currentIndex)} type="button">
        <img
          src={image.src}
          width={image.width}
          height={image.height}
          className={styles.image}
          alt="Listing"
          loading="lazy"
        />
      </button>
      {imageCount > 1 && (
        <>
          <Button
            icon={<ChevronIcon direction="left" />}
            onClick={onClickPrev}
            className={styles.leftArrow}
            disabled={isFirstImage}
          />
          <Button
            icon={<ChevronIcon direction="right" />}
            onClick={onClickNext}
            className={styles.rightArrow}
            disabled={isLastImage}
          />

          <Button
            icon={<Camera size="1x" sizeClass={styles.cameraIcon} />}
            className={styles.moreImageBtn}
            onClick={onButtonClick}
          >
            <span>{`${currentIndex + 1} / ${imageCount}`}</span>
          </Button>
        </>
      )}
    </div>
  );
};

export default DesktopImageView;
